import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../lib/api';
import { Icon } from '../lib/icons';

interface Hero {
  id: string;
  slug: string;
  deceasedName: string;
  birthDate: string | null;
  deathDate: string | null;
  epitaph: string | null;
  biography: string | null;
  heroCategory: string | null;
  heroEra: string | null;
  portraitUrl: string | null;
  coverUrl: string | null;
  galleryUrls: string[];
  tributeCount: number;
}

export default function Heroes() {
  const [heroes, setHeroes] = useState<Hero[]>([]);
  const [active, setActive] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api<{ heroes: Hero[] }>('/memorials/heroes').then((res) => {
      if (res.ok && res.data) setHeroes(res.data.heroes);
      setLoading(false);
    });
  }, []);

  // 分类由数据动态推导，后台新增分类后前端自动出现，无需改代码
  const categories = Array.from(
    new Set(heroes.map((h) => h.heroCategory).filter(Boolean))
  ) as string[];
  const current = active || categories[0] || '';
  const filtered = heroes.filter((h) => h.heroCategory === current);

  return (
    <div className="min-h-screen bg-gradient-to-b from-ink-100 via-ink-50 to-ink-100">
      <div
        aria-hidden
        className="pointer-events-none absolute left-1/2 top-0 h-[420px] w-[720px] -translate-x-1/2 rounded-full bg-candle-300/15 blur-3xl"
      />

      <header className="relative mx-auto flex max-w-5xl items-center justify-between px-6 py-6">
        <Link to="/" className="flex items-center gap-2">
          <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-ink-900 text-xs text-candle-300">
            墓
          </span>
          <span className="text-sm text-ink-700">线上墓迪</span>
        </Link>
        <Link to="/memorials" className="text-xs text-ink-600 underline">
          我的纪念馆
        </Link>
      </header>

      <main className="relative mx-auto max-w-5xl px-6 pb-24">
        <div className="mt-10 text-center">
          <p className="text-xs tracking-[0.3em] text-ink-500">HEROES MEMORIAL</p>
          <h1 className="mt-5 font-serifcn text-3xl text-ink-900 sm:text-4xl">英雄纪念堂</h1>
          <p className="mx-auto mt-5 max-w-xl text-sm leading-relaxed text-ink-600">
            为在民族危难之际舍生取义的英烈立馆。
            <br className="hidden sm:block" />
            点一盏灯、献一束花，让后人记得他们。
          </p>
        </div>

        <div className="mt-10 flex justify-center gap-2">
          {categories.map((c) => (
            <button
              key={c}
              onClick={() => setActive(c)}
              className={`rounded-full px-5 py-2 text-sm transition ${
                current === c
                  ? 'bg-ink-900 text-ink-50'
                  : 'bg-ink-100 text-ink-600 hover:bg-ink-200'
              }`}
            >
              {c}
            </button>
          ))}
        </div>

        {loading && <p className="mt-16 text-center text-sm text-ink-500">加载中…</p>}

        {!loading && filtered.length === 0 && (
          <p className="mt-16 text-center text-sm text-ink-500">暂无内容</p>
        )}

        <div className="mt-10 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {filtered.map((h) => (
            <Link
              key={h.id}
              to={`/m/${h.slug}`}
              className="group rounded-2xl border border-ink-200 bg-white/80 p-5 transition hover:border-ink-400 hover:shadow-sm"
            >
              <div className="flex items-start gap-3">
                <div className="flex h-12 w-12 flex-shrink-0 items-center justify-center overflow-hidden rounded-full bg-ink-100">
                  {h.portraitUrl ? (
                    <img src={h.portraitUrl} alt="" className="h-full w-full object-cover" />
                  ) : (
                    <span className="text-xl"><Icon name="person" /></span>
                  )}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-start justify-between">
                    <h2 className="font-serifcn text-lg text-ink-900">{h.deceasedName}</h2>
                    {h.heroEra && (
                      <span className="ml-2 mt-1 flex-shrink-0 rounded-full bg-ink-100 px-2 py-0.5 text-[10px] text-ink-600">
                        {h.heroEra}
                      </span>
                    )}
                  </div>
                  <p className="mt-1 text-xs text-ink-500">
                    {h.birthDate ?? '?'} — {h.deathDate ?? '?'}
                  </p>
                </div>
              </div>

              {h.epitaph && (
                <p className="mt-4 font-serifcn text-sm text-ink-700">「{h.epitaph}」</p>
              )}

              {h.biography && (
                <p className="mt-2 line-clamp-3 text-xs leading-relaxed text-ink-500">
                  {h.biography}
                </p>
              )}

              {h.galleryUrls && h.galleryUrls.length > 0 && (
                <div className="mt-3 flex gap-2 overflow-x-auto">
                  {h.galleryUrls.map((u, i) => (
                    <img
                      key={i}
                      src={u}
                      alt=""
                      className="h-16 w-20 flex-shrink-0 rounded-lg border border-ink-100 object-cover"
                    />
                  ))}
                </div>
              )}

              <div className="mt-4 flex items-center justify-between border-t border-ink-100 pt-3">
                <span className="text-xs text-ink-500">已被祭奠 {h.tributeCount} 次</span>
                <span className="text-xs text-ink-700 transition group-hover:translate-x-1">
                  前往祭奠 →
                </span>
              </div>
            </Link>
          ))}
        </div>

        <p className="mt-14 text-center text-[11px] leading-relaxed text-ink-400">
          纪念内容采用客观史实性描述，正式上线前请以权威史料核实。
          <br />
          如发现内容有误或有遗漏的英烈，欢迎联系我们更正补充。
        </p>
      </main>
    </div>
  );
}
